/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  TextField, 
  Button, 
  Typography, 
  Paper, 
  Grid, 
  Link, 
  Box, 
  CircularProgress, 
  Alert,
  AlertTitle,
  Snackbar
} from '@mui/material';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showVerificationAlert, setShowVerificationAlert] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const { login, isAuthenticated, error, loading, clearError, user, resendVerificationEmail } = useAuth();
  const navigate = useNavigate();

  // Check if user is logged in but email is not verified
  useEffect(() => {
    if (isAuthenticated && user && user.isEmailVerified === false) {
      setShowVerificationAlert(true);
    } else {
      setShowVerificationAlert(false);
    }
  }, [isAuthenticated, user]);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Don't redirect if showing verification alert
      if (showVerificationAlert) {
        return;
      }
      
      // Check if there's a pending invite
      const pendingInvite = localStorage.getItem('pendingInvite');
      if (pendingInvite) {
        // Clear the pending invite from localStorage
        localStorage.removeItem('pendingInvite');
        // Redirect to the invite page
        navigate(`/invite/${pendingInvite}`);
        return;
      }
      
      // Check if there's a pending team invite
      const pendingTeamInvite = localStorage.getItem('pendingTeamInvite');
      if (pendingTeamInvite) {
        // Clear the pending team invite from localStorage
        localStorage.removeItem('pendingTeamInvite');
        // Redirect to the team invite page
        navigate(`/invite/team/${pendingTeamInvite}`);
        return;
      }
      
      // If no pending invites, redirect to home
      navigate('/home');
    }
  }, [isAuthenticated, navigate, showVerificationAlert]);

  // Clear any previous errors when component mounts (but not if we just got an error)
  useEffect(() => {
    // Only clear errors on initial mount, not when error state changes
    if (!error) {
      clearError();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional: run only on mount to clear stale errors
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      await login(email, password);
      // If login is successful, useAuth will update isAuthenticated and the useEffect above will handle navigation
    } catch (error) {
      logger.error('Login error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingEmail(true);
    try {
      await resendVerificationEmail();
      setResendSuccess(true);
    } catch (error) {
      logger.error('Error resending verification email:', error);
    } finally {
      setResendingEmail(false);
    }
  };

  const handleCloseSnackbar = () => {
    setResendSuccess(false);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Grid 
      container 
      justifyContent="center" 
      alignItems="center" 
      style={{ minHeight: 'calc(100vh - 140px)' }}
    >
      <Grid item xs={12} sm={8} md={6} lg={4}>
        <Paper elevation={3} style={{ padding: '2rem', maxWidth: '500px', margin: '0 auto' }}>
          <Typography variant="h4" component="h1" align="center" gutterBottom>
            {t('login')}
          </Typography>
          
          {error && (
            <Typography color="error" align="center" paragraph>
              {error}
            </Typography>
          )}
          
          {showVerificationAlert && (
            <Alert 
              severity="warning" 
              sx={{ mb: 3 }}
              action={
                <Button 
                  color="inherit" 
                  size="small" 
                  onClick={handleResendVerification}
                  disabled={resendingEmail}
                >
                  {resendingEmail ? <CircularProgress size={20} /> : t('resendEmail').toUpperCase()}
                </Button>
              }
            >
              <AlertTitle>{t('emailNotVerified')}</AlertTitle>
              {t('pleaseVerifyEmail')}
            </Alert>
          )}
          
          <form onSubmit={handleSubmit}>
            <TextField
              label={t('emailAddress')}
              type="email"
              fullWidth
              margin="normal"
              variant="outlined"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label={t('password')}
              type="password"
              fullWidth
              margin="normal"
              variant="outlined"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              InputLabelProps={{ shrink: true }}
            />
            
            <Box textAlign="right" mt={1} mb={2}>
              <Link component={RouterLink} to="/forgot-password" variant="body2">
                {t('forgotPassword')}
              </Link>
            </Box>
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              size="large"
              style={{ marginTop: '1rem' }}
              disabled={isSubmitting}
            >
              {isSubmitting ? <CircularProgress size={24} /> : t('login')}
            </Button>
          </form>
          
          <Box mt={3} textAlign="center">
            <Typography variant="body2">
              {t('dontHaveAccount')}{' '}
              <Link component={RouterLink} to="/register">
                {t('register')}
              </Link>
            </Typography>
            <Box mt={2}>
              <Typography variant="body2">
                <Link component={RouterLink} to="/" variant="body2">
                  {t('backToLandingPage')}
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Grid>
      
      <Snackbar
        open={resendSuccess}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        message={t('verificationEmailSentSuccess')}
      />
    </Grid>
  );
};

export default LoginPage; 